import React, { useEffect, useRef, useState } from 'react';
import { styled } from '@uhg-abyss/web/tools/styled';
import { V2FileUpload } from '@uhg-abyss/web/ui/FileUpload';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { useFormV2 } from '@uhg-abyss/web/hooks/useForm';
import { useTinRegistration } from '@src/state/tinRegistration';
import { useUser } from '@src/state/user/hooks';
import { getFileType } from '@src/state/tinRegistration/utils';

const MAX_UPLOAD_FILE_NAME_LENGTH = 54;
const MAX_UPLOAD_FILE_SIZE_BYTES = 5 * 1024 * 1024;
const PASSWORD_PROTECTED_UPLOAD_MESSAGE =
  'Upload failed: This file is password-protected and cannot be processed. Please remove the password and try uploading again.';
const DEFAULT_UPLOAD_ERROR_MESSAGE =
  "Service unavailable: We're sorry, your upload was unsuccessful. Please try again later. (D-001)";

const getVerifyTinUploadErrorMessage = (error) => {
  const messageCandidates = [
    error?.message,
    error?.data?.message,
    error?.response?.data?.message,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  const faultCodeCandidates = [
    error?.faultCode,
    error?.data?.faultCode,
    error?.response?.data?.faultCode,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  const isPasswordProtectedError =
    /password[ -]?protected|encrypted|cannot be processed/.test(
      messageCandidates
    ) ||
    /password|encrypt/.test(faultCodeCandidates) ||
    faultCodeCandidates.includes('doduee');

  if (isPasswordProtectedError) {
    return PASSWORD_PROTECTED_UPLOAD_MESSAGE;
  }

  return error?.message || DEFAULT_UPLOAD_ERROR_MESSAGE;
};

const FILE_UPLOAD_CONTENT_WIDTH_CSS = { width: '100%', maxWidth: '380px' };
const FILE_UPLOAD_CSS = {
  'abyss-file-upload-drop-zone': FILE_UPLOAD_CONTENT_WIDTH_CSS,
  'abyss-file-input-file-list-list': FILE_UPLOAD_CONTENT_WIDTH_CSS,
  'abyss-alert-root': FILE_UPLOAD_CONTENT_WIDTH_CSS,
  'abyss-file-upload-file-list-item': { wordBreak: 'break-word' },
};

const FileRow = styled('div', {
  width: '100%',
  maxWidth: '460px',
  border: '1px solid $core.color.neutral.40',
  borderRadius: '8px',
  padding: '8px 12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '10px',
  backgroundColor: '$white',
});

const FileName = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  fontSize: '14px',
  fontWeight: 500,
  color: '$core.color.neutral.90',
  maxWidth: '460px',
  wordBreak: 'break-word',
});

const RemoveFileButton = styled('button', {
  border: 'none',
  background: 'transparent',
  color: '$core.color.neutral.70',
  width: '20px',
  height: '20px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  padding: 0,
  borderRadius: '2px',
  '&:hover': {
    color: '$core.color.neutral.90',
  },
});

const FileUploadV2 = ({ validators, ...rest }) => (
  <V2FileUpload
    css={FILE_UPLOAD_CSS}
    variant="minimal"
    fileTypes={{ 'application/pdf': ['.pdf'] }}
    validators={
      validators
        ? {
            ...validators,
            validate: {
              ...validators.validate,
              maxFiles: (files) =>
                files && files.length > 1 ? 'Maximum 1 file allowed.' : true,
              fileLength: (files) =>
                files.some(
                  (file) => file.name.length > MAX_UPLOAD_FILE_NAME_LENGTH
                )
                  ? 'File Name cannot exceed 50 characters in length.'
                  : true,
              fileSize: (files) =>
                files.some((file) => file.size > MAX_UPLOAD_FILE_SIZE_BYTES)
                  ? 'File is larger than 5 MB.'
                  : true,
            },
          }
        : {}
    }
    {...rest}
  />
);

export const LocationProofUpload = ({
  model,
  label,
  validators,
  setDoc360UploadErrors,
  onProofStateChange,
}) => {
  const form = useFormV2();
  const { isCheckoutUser } = useUser.state();
  const { documentIds, trackingNumber } = useTinRegistration.state();
  const { uploadProof, setDocumentId, setDocumentLoading } =
    useTinRegistration.actions();
  const [visibleFileName, setVisibleFileName] = useState('');
  const [showRestoredFileRow, setShowRestoredFileRow] = useState(false);
  const uploadVersionRef = useRef(0);
  const onProofStateChangeRef = useRef(onProofStateChange);
  const modelDoc360Id = documentIds?.[model];

  useEffect(() => {
    onProofStateChangeRef.current = onProofStateChange;
  }, [onProofStateChange]);

  useEffect(() => {
    const value = form.getValues(model);
    const file = Array.isArray(value) ? value[0] : value;
    const nextFileName = file?.name || '';
    const hasRestoredDraftFile = !!nextFileName && !!documentIds?.[model];
    setVisibleFileName(hasRestoredDraftFile ? nextFileName : '');
    setShowRestoredFileRow(hasRestoredDraftFile);
  }, []);

  useEffect(() => {
    // Emit only when a doc360Id is present; clear/error paths already emit explicitly.
    if (modelDoc360Id) {
      onProofStateChangeRef.current?.();
    }
  }, [modelDoc360Id]);

  const clearDoc360Error = () => {
    setDoc360UploadErrors((curr) => {
      const updated = { ...curr };
      delete updated[model];
      return updated;
    });
  };

  const setDoc360Error = (message) => {
    setDoc360UploadErrors((curr) => ({
      ...curr,
      [model]: message,
    }));
  };

  const emitProofChange = () => {
    onProofStateChangeRef.current?.();
  };

  const stopLoadingAndEmitChange = () => {
    setDocumentLoading({ model, value: false });
    emitProofChange();
  };

  const setUploadErrorAndEmitChange = (message) => {
    setDocumentLoading({ model, value: false });
    setDoc360Error(message);
    setDocumentId({ id: model, value: undefined });
    emitProofChange();
  };

  const handleChange = () => {
    uploadVersionRef.current += 1;
    const uploadVersion = uploadVersionRef.current;

    const uploadedValue = form.getValues(model);
    const uploadedFiles = Array.isArray(uploadedValue)
      ? uploadedValue.filter(Boolean)
      : uploadedValue
      ? [uploadedValue]
      : [];
    const uploadedFile = uploadedFiles[0] || null;

    // Always reset on any change
    clearDoc360Error();
    setDocumentId({ id: model, value: undefined });

    if (!uploadedFile) {
      setVisibleFileName('');
      setShowRestoredFileRow(false);
      stopLoadingAndEmitChange();
      return;
    }

    // If this is a restored draft file object, preserve its existing doc360Id.
    if (!(uploadedFile instanceof File)) {
      const restoredDocId = uploadedFile?.doc360Id || documentIds?.[model];
      if (uploadedFile?.name && restoredDocId) {
        setVisibleFileName(uploadedFile.name);
        setShowRestoredFileRow(true);
        setDocumentId({ id: model, value: restoredDocId });
        setDocumentLoading({ model, value: false });
        return;
      }
      // Otherwise, it's an error
      setUploadErrorAndEmitChange(DEFAULT_UPLOAD_ERROR_MESSAGE);
      return;
    }

    setVisibleFileName('');
    setShowRestoredFileRow(false);

    if (uploadedFiles.length !== 1) {
      stopLoadingAndEmitChange();
      return;
    }

    const isInvalidFile =
      uploadedFile.size > MAX_UPLOAD_FILE_SIZE_BYTES ||
      uploadedFile.name.length > MAX_UPLOAD_FILE_NAME_LENGTH;
    if (isInvalidFile) {
      stopLoadingAndEmitChange();
      return;
    }

    const registration = form.getValues('searchRegistration');
    const fileType = getFileType(model);

    if (!fileType) {
      setUploadErrorAndEmitChange(DEFAULT_UPLOAD_ERROR_MESSAGE);
      return;
    }

    setDocumentLoading({ model, value: true });
    uploadProof(
      {
        billingTin: registration?.billingTin,
        requestId: registration?.requestId,
        fileType,
        trackingNumber,
        file: uploadedFile,
        test: isCheckoutUser,
        model,
      },
      (resp) => {
        if (uploadVersionRef.current !== uploadVersion) {
          return;
        }

        if (resp?.doc360Id) {
          setDocumentLoading({ model, value: false });
          setDocumentId({ id: model, value: resp.doc360Id });
          return;
        }

        setUploadErrorAndEmitChange(
          'Upload failed: A document ID could not be generated. Please try again later. (D-001)'
        );
      },
      (error) => {
        if (uploadVersionRef.current !== uploadVersion) {
          return;
        }

        setUploadErrorAndEmitChange(getVerifyTinUploadErrorMessage(error));
      }
    );
  };

  const handleRemoveVisibleFile = () => {
    uploadVersionRef.current += 1;
    form.setValue(model, [], {
      touched: false,
      errors: { required: true },
    });
    setVisibleFileName('');
    setShowRestoredFileRow(false);
    clearDoc360Error();
    setDocumentId({ id: model, value: undefined });
    setDocumentLoading({ model, value: false });
    emitProofChange();
  };

  return (
    <>
      <FileUploadV2
        model={model}
        label={label}
        subText="Must be a PDF under 5MB and not password-protected."
        validators={validators}
        onChange={handleChange}
      />
      {showRestoredFileRow && visibleFileName && (
        <FileRow>
          <FileName>
            <span>{visibleFileName}</span>
          </FileName>
          <RemoveFileButton type="button" onClick={handleRemoveVisibleFile}>
            <IconSymbol icon="close" variant="outlined" size="16px" />
          </RemoveFileButton>
        </FileRow>
      )}
    </>
  );
};
